/**
 * 관리자 학습내용 본문(IndexedDB).
 * LS에는 version 메타만 두고, 대용량 rows/templates JSON은 여기 저장.
 */

const DB_NAME = 'ClassPlannerAdminLearningContent';
const DB_VERSION = 1;
const STORE_ROWS = 'rows';
const STORE_TEMPLATES = 'templates';

type StoreName = typeof STORE_ROWS | typeof STORE_TEMPLATES;

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ROWS)) {
        db.createObjectStore(STORE_ROWS);
      }
      if (!db.objectStoreNames.contains(STORE_TEMPLATES)) {
        db.createObjectStore(STORE_TEMPLATES);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet<T>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(store, 'readonly');
      const r = tx.objectStore(store).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as T | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(store: StoreName, key: string, value: unknown): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const r = tx.objectStore(store).put(value, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function idbDelete(store: StoreName, key: string): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const r = tx.objectStore(store).delete(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function idbClearStore(store: StoreName): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const r = tx.objectStore(store).clear();
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

export async function idbGetAdminLearningRowsBody<T>(key: string): Promise<T | undefined> {
  try {
    return await idbGet<T>(STORE_ROWS, key);
  } catch {
    return undefined;
  }
}

export async function idbPutAdminLearningRowsBody(key: string, value: unknown): Promise<void> {
  try {
    await idbPut(STORE_ROWS, key, value);
  } catch {
    /* quota / private mode */
  }
}

export async function idbDeleteAdminLearningRowsBody(key: string): Promise<void> {
  try {
    await idbDelete(STORE_ROWS, key);
  } catch {
    /* ignore */
  }
}

export async function idbGetAdminLearningTemplatesBody<T>(key: string): Promise<T | undefined> {
  try {
    return await idbGet<T>(STORE_TEMPLATES, key);
  } catch {
    return undefined;
  }
}

export async function idbPutAdminLearningTemplatesBody(key: string, value: unknown): Promise<void> {
  try {
    await idbPut(STORE_TEMPLATES, key, value);
  } catch {
    /* quota / private mode */
  }
}

export async function idbDeleteAdminLearningTemplatesBody(key: string): Promise<void> {
  try {
    await idbDelete(STORE_TEMPLATES, key);
  } catch {
    /* ignore */
  }
}

/** 개발·마이그레이션용 — 본문 스토어 전체 비우기 */
export async function clearAdminLearningContentIdb(): Promise<void> {
  try {
    await idbClearStore(STORE_ROWS);
    await idbClearStore(STORE_TEMPLATES);
  } catch {
    /* ignore */
  }
}
